import { useCurrentStateByControlUnitId } from '../../../../../../redux/controlUnit/hooks/useCurrentStateByControlUnitId';
import { useShowErrorMessage } from '../../../../../../handlingErrors';
import _ from 'lodash';
import { getPolyglot } from '../../../../../../i18n';
import { Merchandiser } from '../../../../redux/composableDevice/model/Merchandiser.model';

import usePreferredTemperatureUnit from '../../../../../../redux/settings/hooks/usePreferredTemperatureUnit';
import { useAppSelector } from '../../../../../../redux/store.model';
import { getMachineTypeSelector } from '../../../../../../redux/composableDevice/selectors/composableDeviceSelectors';
import { ChannelMap } from '../../../../../../redux/groups/api/group.model';
import {
  getDecimals,
  getValueWithPreferredUnitAndDecimals,
} from '../../../../../../redux/controlUnit/api/util/sensor/formatValues';
import { DEFAULT_VALUE_SENSOR } from '../../../../../../redux/controlUnit/api/util/constants';

export interface ChannelVirtualSuperHeatProps {
  merchandiser: Merchandiser;
  channel: (ChannelMap & { name?: string }) | null;
}
function useChannelVirtualSuperHeat(props: ChannelVirtualSuperHeatProps) {
  const { merchandiser, channel } = props;
  const polyglot = getPolyglot();
  const showErrorMessage = useShowErrorMessage();

  const machineType = useAppSelector((state) =>
    getMachineTypeSelector(state, {
      machineTypeId: merchandiser?.composableDevice?.machineTypeId,
    })
  );
  const labels = machineType?.labels ?? {};

  const channnelIndex: string =
    Object.entries(labels)
      .find(([_key, value]) => value === channel?.name)?.[0]
      .split('.')[1] ?? '';

  const channnelIndexNumber = Number(channnelIndex);

  const channels = merchandiser?.composableDevice?.channels;

  const channelName = merchandiser.channels?.[channnelIndexNumber]?.name;
  const indexA = Number(labels[`channels.${channnelIndexNumber}.virtual.a`]);
  const indexB = Number(labels[`channels.${channnelIndexNumber}.virtual.b`]);

  // 2 because 3rd first channels are actions
  const suctionTemperatureChannel = channels?.[indexA];
  const lowPressureTemperatureChannel = channels?.[indexB];

  const suctionTemperatureChannelId = suctionTemperatureChannel?.channel ?? '';
  const lowPressureTemperatureChannelId =
    lowPressureTemperatureChannel?.channel ?? '';

  const {
    currentSensorData: currentSensorDataSuction,
    loading: loadingSuction,
    error: errorSuction,
  } = useCurrentStateByControlUnitId(
    suctionTemperatureChannel?.deviceId ?? '',
    suctionTemperatureChannel?.controlUnitId ?? '',
    showErrorMessage
  );

  const {
    currentSensorData: currentSensorDataLowPressure,
    loading: loadingLowPressure,
    error: errorLowPressure,
  } = useCurrentStateByControlUnitId(
    lowPressureTemperatureChannel?.deviceId ?? '',
    lowPressureTemperatureChannel?.controlUnitId ?? '',
    showErrorMessage
  );

  const sensorSuction = currentSensorDataSuction?.[suctionTemperatureChannelId];
  const sensorLowPressure =
    currentSensorDataLowPressure?.[lowPressureTemperatureChannelId];
  const sensorUnit = sensorSuction?.metadata?.unit ?? '';
  const sensorUnitString =
    sensorSuction?.metadata?.unitString ?? sensorSuction?.metadata?.unit ?? '';
  const sensorLowPressureUnitString =
    sensorLowPressure?.metadata?.unitString ??
    sensorLowPressure?.metadata?.unit ??
    '';
  const { data: preferredTemperatureUnit } = usePreferredTemperatureUnit();
  const { value: sensorValueSuction } = getValueWithPreferredUnitAndDecimals(
    sensorSuction?.value,
    sensorUnitString,
    0,
    preferredTemperatureUnit
  );
  const { value: sensorValueLowPressure } =
    getValueWithPreferredUnitAndDecimals(
      sensorLowPressure?.value,
      sensorLowPressureUnitString,
      0,
      preferredTemperatureUnit
    );

  const unitDecimals =
    sensorSuction?.metadata?.decimals ??
    sensorLowPressure?.metadata?.decimals ??
    0;
  const decimals = getDecimals(unitDecimals, preferredTemperatureUnit);
  const sensorValueRounded = isNaN(Number(sensorValueSuction))
    ? DEFAULT_VALUE_SENSOR
    : Number(sensorValueSuction);
  const sensorValueLowPressureRounded = isNaN(Number(sensorValueLowPressure))
    ? DEFAULT_VALUE_SENSOR
    : Number(sensorValueLowPressure);

  const value =
    _.isNumber(sensorValueRounded) && _.isNumber(sensorValueLowPressureRounded)
      ? sensorValueRounded - sensorValueLowPressureRounded
      : DEFAULT_VALUE_SENSOR;

  const valueText =
    value ?? polyglot.t('device.parameters.no_translation_available');
  const loading = loadingSuction || loadingLowPressure;
  return {
    value: value,
    valueString: valueText,
    unit: sensorUnit,
    unitString: sensorUnitString,
    loading: loading && value == undefined,
    sensorSuction: sensorSuction,
    sensorLowPressure: sensorLowPressure,
    error: errorLowPressure || errorSuction,
    name: channelName,
    decimals: decimals,
  };
}

export default useChannelVirtualSuperHeat;
